import { Card, CardContent, CardHeader } from '@/components/ui';
import type { ErrorListItem } from '@/types/api';

import { ResolutionPanel } from '../ErrorDetail';
import { GuidancePanel } from './GuidancePanel';

/** Spec S08 §"Layout D-type" — 4-column conflict review. */
export function ConflictReviewLayout({ error }: { error: ErrorListItem }) {
  const payload = error.payload as Record<string, unknown>;
  return (
    <div className="grid h-full grid-cols-[25%_25%_25%_25%] gap-3 p-3">
      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">New assertion</h3>
        </CardHeader>
        <CardContent>
          <Field label="Value" value={String(payload.new_value ?? '—')} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Existing assertion</h3>
        </CardHeader>
        <CardContent>
          <Field label="Value" value={String(payload.existing_value ?? '—')} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Conflict context</h3>
        </CardHeader>
        <CardContent>
          <Field label="Subject" value={String(payload.subject_uri ?? '—')} />
          <Field label="Predicate" value={String(payload.predicate_uri ?? '—')} />
          <Field label="Conflict class" value={String(error.error_subtype ?? '—')} />
          <Field
            label="Suggested resolution"
            value={String(payload.resolution_suggestion ?? '—')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">Resolve</h3>
        </CardHeader>
        <CardContent>
          {error.guidance && <GuidancePanel guidance={error.guidance} />}
          <ResolutionPanel
            error={error}
            availableActions={['fix_and_accept', 'cancel', 'suspend']}
            decisionTypes={{
              fix_and_accept: [
                'temporal_precedence',
                'source_quality',
                'context_enrichment',
                'scope_difference',
                'data_error',
              ],
              cancel: ['ontology_ambiguity', 'data_error'],
              suspend: ['deferred'],
              escalate: ['skill_escalation'],
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-2">
      <p className="text-h4 text-slate-500">{label}</p>
      <p className="break-all font-mono text-small text-slate-700">{value}</p>
    </div>
  );
}
